'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useState } from 'react'

// walrus
import Box from '@components/kit/box'
import Text from '@components/kit/text'
import Pressable from '@components/kit/pressable'
import { CheckCircleIcon, ChevronDownIcon } from '@components/kit/icons'
import Circle from '@components/kit/circle'

// lib
import { ISkill, ISkillsResponse, IUserSkill } from '@store/Skills/types'
import { LabelText, RowWrapper } from '@components/table'

/* -----------------------------------------------------------------------------
 * ProgressBar
 * -------------------------------------------------------------------------- */

function ProgressBar({ percentage }: { percentage: number }) {
  const clampedPct = Math.min(100, Math.max(0, percentage))
  return (
    <Box
      width='100%'
      height={8}
      borderRadius='pill'
      bg='shade02'
      overflowX='hidden'
      overflowY='hidden'
    >
      <Box
        height='100%'
        borderRadius='pill'
        width={`${clampedPct}%`}
        bg='walrusGreen500'
      />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * SkillItem
 * -------------------------------------------------------------------------- */

function SkillItem({
  item,
  status
}: {
  item: {
    title: string
  }
  status: 'available' | 'pending' | 'finished'
}) {
  return (
    <Box flexDirection='row' alignItems='center' gap='08' marginBottom='08'>
      {status === 'finished' ? (
        <Box
          width={16}
          height={16}
          borderRadius='pill'
          bg='lightGreen'
          alignItems='center'
          justifyContent='center'
        >
          <CheckCircleIcon width={16} height={16} fill='lightGreen' />
        </Box>
      ) : (
        <Circle
          size={12}
          bg={status === 'pending' ? 'lightOrange' : 'white'}
          borderWidth={2}
          borderColor={status === 'pending' ? 'paleYellow' : 'shade05'}
        />
      )}

      <Text
        font='text.14.medium'
        textColor={status === 'finished' ? 'shade07' : 'shade05'}
      >
        {item.title}
      </Text>
    </Box>
  )
}

function normalizeAvailableSkill(skill: ISkill) {
  return {
    title: skill.title
  }
}

function normalizeUserSkill(userSkill: IUserSkill) {
  return {
    title: userSkill.skill?.title || ''
  }
}

/* -----------------------------------------------------------------------------
 * SkillBuilder
 * -------------------------------------------------------------------------- */

interface SkillBuilderProps {
  skills: ISkillsResponse | null
}

function SkillBuilder({ skills }: SkillBuilderProps) {
  const [expanded, setExpanded] = useState(false)

  const totalSkills = skills
    ? skills.available.length + skills.pending.length + skills.finished.length
    : 0
  const finishedCount = skills?.finished.length ?? 0
  const percentage =
    totalSkills > 0 ? Math.round((finishedCount / totalSkills) * 100) : 0

  return (
    <>
      <RowWrapper>
        <LabelText font='display.14.bold'>Skill Builder</LabelText>
      </RowWrapper>

      {totalSkills > 0 ? (
        <Box px='16' pb='16' pt='12'>
          <Box flexDirection='row' alignItems='center' marginBottom='08'>
            <Text font='text.14.bold' textColor='shade06' flex='1 1 auto'>
              Current Progress
            </Text>
            <Text font='text.14.bold' textColor='shade06'>
              {percentage}%
            </Text>
          </Box>

          <Box marginBottom='16'>
            <ProgressBar percentage={percentage} />
          </Box>

          <Pressable onPress={() => setExpanded(prev => !prev)}>
            <Box
              flexDirection='row'
              alignItems='center'
              alignSelf='flex-start'
              gap='08'
              px='12'
              py='04'
              borderRadius='pill'
              bg={expanded ? 'shade02' : 'paleViolet'}
            >
              <Text font='text.12.medium' textColor='shade06'>
                {finishedCount} of {totalSkills} skills completed this month
              </Text>
              <Box
                style={{
                  transform: [{ rotate: expanded ? '180deg' : '0deg' }]
                }}
              >
                <ChevronDownIcon fill='shade06' />
              </Box>
            </Box>
          </Pressable>

          {expanded && (
            <Box marginTop='12'>
              {skills.finished.map(skill => (
                <SkillItem
                  key={`finished-${skill._id}`}
                  item={normalizeUserSkill(skill)}
                  status='finished'
                />
              ))}
              {skills.pending.map(skill => (
                <SkillItem
                  key={`pending-${skill._id}`}
                  item={normalizeUserSkill(skill)}
                  status='pending'
                />
              ))}
              {skills.available.map(skill => (
                <SkillItem
                  key={`available-${skill._id}`}
                  item={normalizeAvailableSkill(skill)}
                  status='available'
                />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <RowWrapper>
          <Text font='text.14.medium' textColor='newMutedPurple'>
            This member has no skills available.
          </Text>
        </RowWrapper>
      )}
    </>
  )
}

export default SkillBuilder
